import './Dropdown.scss'

import Downshift, {ControllerStateAndHelpers} from 'downshift'
import React, {
	ReactElement,
	createContext,
	useContext,
	useEffect,
	useState
} from 'react'

import {Icon} from './Icon'
import {Mod} from 'types'
import {style} from 'util/style'

const styles = style('dropdown', ['list', 'item', 'icon'])

export type OptionType = {
	amount: number | null
	title: string
	path: string
	id?: number
	subCategory?: boolean
}
const DropDownContext = createContext<ControllerStateAndHelpers<any>>(null)

type DropdownType = {
	mod?: Mod<'boldtext' | 'border' | 'center' | 'small'>
	items: Array<OptionType>
	// Different options, optimized for codeurs cms
	onChange: (selected: string) => void
	onInputChange?: (query: string) => void
	selected?: OptionType
	iconComponent?: ReactElement
	// Shows a custom icon on dropdown
	showItemsOnHover?: boolean
	// Shows items on hover instead of onclick
	itemPropertyToUse?: string
	// Sets which property to use as identifier in items Array, default is 'path'. Useful when you need to return an 'id' key
	itemPropertyToReturn?: string
	// Sets which property to return as visible Value. Default is 'title'.
	customInputId?: string
	// Adds an id on the input tag -- useful for linking external lable to this input
	customLabelName?: string
	// Adds a name on the label tag
	defaultText?: string
	// Remove key
	allowClearSelection?: boolean
	clearInputOnClick?: boolean
	enableFilter?: boolean
	noMaxHeight?: boolean
	// smaller mod for dropdown items
	smallItems?: boolean
}

export const Dropdown: React.FC<DropdownType> = ({
	items,
	onChange,
	onInputChange,
	selected,
	iconComponent = null,
	showItemsOnHover = false,
	itemPropertyToUse = 'path',
	itemPropertyToReturn = 'title',
	mod,
	customInputId,
	customLabelName,
	defaultText,
	clearInputOnClick = true,
	enableFilter = false,
	allowClearSelection = false,
	noMaxHeight = false,
	smallItems
}) => {
	const [dropdownIsOpen, setDropdownOpen] = useState(false)
	const [inputChanged, setInputChanged] = useState(false)
	const [inputFieldValue, setInputvalue] = useState(
		selected?.[itemPropertyToReturn] || ''
	)
	return (
		<Downshift
			onOuterClick={() => setDropdownOpen(false)}
			isOpen={dropdownIsOpen}
			itemToString={(item) => item && item[itemPropertyToReturn]}
			initialSelectedItem={
				!selected ? (defaultText ? defaultText : items[0]) : selected
			}
			onChange={(e) => {
				onChange(e && e[itemPropertyToUse])
				setDropdownOpen(false)
			}}
			onInputValueChange={(val) => {
				setInputvalue(val)
				if (!enableFilter) return
				setInputChanged(true)
			}}
			inputValue={
				inputFieldValue && selected?.amount
					? `${inputFieldValue} (€ ${selected.amount})`
					: inputFieldValue
			}
			selectedItem={selected}
			stateReducer={handleState}
		>
			{(downshift) => {
				const hasItems = items?.length > 0
				const {
					selectedItem,
					isOpen,
					getMenuProps,
					getLabelProps,
					getInputProps,
					inputValue,
					clearSelection
				} = downshift
				useEffect(() => {
					if (!selectedItem && allowClearSelection) clearSelection()
				}, [selectedItem])
				return (
					<div
						className={styles
							.is({open: isOpen})
							.mod({hoverable: showItemsOnHover})
							.mod(mod)
							.mod({typing_disabled: !enableFilter})
							.mod({nomaxheight: noMaxHeight})}
						aria-expanded={isOpen ? 'true' : 'false'}
					>
						<div className={styles('labelcontainer')}>
							{!customInputId && (
								<label
									className={styles('labelcontainer-label')}
									{...getLabelProps({})}
								>
									{customLabelName}
								</label>
							)}
							<input
								readOnly={!enableFilter}
								className={styles('labelcontainer-input')}
								{...getInputProps({
									placeholder: defaultText,
									id: customInputId && customInputId,
									name: 'dropdown-input',
									onChange: (e) => {
										if (onInputChange) onInputChange(e.target.value)
									},
									onKeyPress: (e) => {
										if (!enableFilter) {
											e.preventDefault()
											return
										}
									},
									onBlur: () => {
										setDropdownOpen(false)
										if ((clearInputOnClick && selectedItem) || enableFilter) {
											setInputvalue(selectedItem?.[itemPropertyToReturn])
										}
									},
									onFocus: (e) => {
										if (!enableFilter) {
											e.preventDefault()
										}
										setDropdownOpen(true)
										if (clearInputOnClick) setInputvalue('')
									},
									onClick: () => {
										setDropdownOpen(true)
										if (clearInputOnClick) setInputvalue('')
									}
								})}
							/>
							<div
								onClick={() => {
									setDropdownOpen(true)
									if (clearInputOnClick) setInputvalue('')
									// if (enableFilter)
								}}
								className={styles('labelcontainer-icon').mod({
									default: !iconComponent
								})}
							>
								<DropdownIcon customIcon={iconComponent} isOpen={isOpen} />
							</div>
						</div>
						<div className={styles('container')}>
							{hasItems && (
								<ul
									className={styles.list}
									aria-label="dropdown"
									{...getMenuProps()}
								>
									{items
										.filter((item) => {
											if (!enableFilter) return true
											if (!inputValue) return true
											if (!inputChanged) return true
											const loweredInput = inputValue.toLowerCase()
											const itemValue = item && item[itemPropertyToReturn]
											const itemValueLowered = itemValue?.toLowerCase()
											if (itemValueLowered.includes(loweredInput)) return true
										})
										.map((item, i) => (
											<DropDownContext.Provider
												value={downshift}
												key={item.path}
											>
												<DropdownItem
													item={item}
													selected={selectedItem === item}
													index={i}
													smallItems={smallItems}
												/>
											</DropDownContext.Provider>
										))}
								</ul>
							)}
						</div>
					</div>
				)
			}}
		</Downshift>
	)
}

export const DropdownIcon: React.FC<{
	customIcon: ReactElement
	isOpen: boolean
}> = ({customIcon, isOpen}) => {
	if (customIcon) return customIcon

	return (
		<div className={styles.icon.is({open: isOpen})}>
			<Icon icon="chevronDown" />
		</div>
	)
}

function handleState(state, changes) {
	switch (changes.type) {
		case Downshift.stateChangeTypes.keyDownEscape:
			return {isOpen: false}
		default:
			return changes
	}
}

export const DropdownItem: React.FC<{
	item: OptionType
	index: number
	selected: boolean
	smallItems: boolean
}> = ({item, index, selected, smallItems}) => {
	const {highlightedIndex, getItemProps} = useContext(DropDownContext)
	const labelWithAmount = `${item.title}${
		item.amount ? ` (€ ${item.amount})` : ''
	}`
	return (
		<li
			key={item?.path || item.title}
			className={styles.item
				.is({
					highlighted: index === highlightedIndex,
					selected
				})
				.mod({subcategory: item?.subCategory, small: smallItems})}
			{...getItemProps({
				index,
				item
			})}
		>
			{labelWithAmount}
		</li>
	)
}
